import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { getCostsDiario } from '../lib/api'
import { useAppStore } from '../stores/app'

interface UsageCost {
  id: string
  organization_id: string
  task_id: string | null
  model: string
  model_type: 'llm' | 'image' | 'research'
  tokens_input: number | null
  tokens_output: number | null
  cost: number
  created_at: string
}

interface CostSummary {
  total_cost: number
  llm_cost: number
  image_cost: number
  research_cost: number
  budget_limit: number
  budget_used_percentage: number
}

export function useCostSummary(days: number = 30) {
  const organizationId = useAppStore((state) => state.organizationId)

  return useQuery({
    queryKey: ['cost_summary', organizationId, days],
    queryFn: async () => {
      if (!organizationId) return null
      
      // Calcular fecha de inicio
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)
      
      // Obtener costos
      const { data: costs, error: costsError } = await supabase
        .from('usage_costs')
        .select('*')
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())

      if (costsError) throw costsError

      // Calcular totales
      let total_cost = 0
      let llm_cost = 0
      let image_cost = 0
      let research_cost = 0

      costs?.forEach((entry) => {
        const cost = Number(entry.cost) || 0
        total_cost += cost

        if (entry.model_type === 'llm') {
          llm_cost += cost
        } else if (entry.model_type === 'image') {
          image_cost += cost
        } else if (entry.model_type === 'research') {
          research_cost += cost
        }
      })

      // Obtener límite presupuestario
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('monthly_budget_limit')
        .eq('id', organizationId)
        .single()

      if (orgError) throw orgError

      const budget_limit = Number(org?.monthly_budget_limit) || 50.0
      const budget_used_percentage = budget_limit > 0 ? (total_cost / budget_limit) * 100 : 0

      return {
        total_cost: Number(total_cost.toFixed(4)),
        llm_cost: Number(llm_cost.toFixed(4)),
        image_cost: Number(image_cost.toFixed(4)),
        research_cost: Number(research_cost.toFixed(4)),
        budget_limit,
        budget_used_percentage: Number(budget_used_percentage.toFixed(2)),
      } as CostSummary
    },
    enabled: !!organizationId,
  })
}

export function useUsageCosts(days: number = 7, limit: number = 100) {
  const organizationId = useAppStore((state) => state.organizationId)

  return useQuery({
    queryKey: ['usage_costs', organizationId, days, limit],
    queryFn: async () => {
      if (!organizationId) return []
      
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)
      
      const { data, error } = await supabase
        .from('usage_costs')
        .select('*')
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data as UsageCost[]
    },
    enabled: !!organizationId,
  })
}

export function useCostsByModel(days: number = 30) {
  const organizationId = useAppStore((state) => state.organizationId)

  return useQuery({
    queryKey: ['costs_by_model', organizationId, days],
    queryFn: async () => {
      if (!organizationId) return []
      
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)
      
      const { data, error } = await supabase
        .from('usage_costs')
        .select('*')
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())

      if (error) throw error

      // Agrupar por modelo
      const byModel: Record<string, { count: number; cost: number }> = {}
      
      data?.forEach((entry) => {
        const model = entry.model || 'unknown'
        if (!byModel[model]) {
          byModel[model] = { count: 0, cost: 0 }
        }
        byModel[model].count += 1
        byModel[model].cost += Number(entry.cost) || 0
      })

      // Convertir a array y ordenar por costo
      return Object.entries(byModel)
        .map(([model, stats]) => ({
          model,
          count: stats.count,
          cost: Number(stats.cost.toFixed(4)),
        }))
        .sort((a, b) => b.cost - a.cost)
    },
    enabled: !!organizationId,
  })
}

export function useCostsDiario(days: number = 30) {
  const organizationId = useAppStore((state) => state.organizationId)

  return useQuery({
    queryKey: ['costs_diario', organizationId, days],
    queryFn: () => getCostsDiario(days),
    enabled: !!organizationId,
  })
}

export function useUpdateBudget() {
  const organizationId = useAppStore((state) => state.organizationId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (newLimit: number) => {
      if (!organizationId) throw new Error('No organization ID')
      
      const { data, error } = await supabase
        .from('organizations')
        .update({ monthly_budget_limit: newLimit })
        .eq('id', organizationId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost_summary', organizationId] })
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId] })
    },
  })
}
